import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    BlockStack, Card, Text, InlineStack, Select, TextField,
    IndexTable, Badge, Spinner, Link, EmptySearchResult, Banner, useIndexResourceState, Modal, Button, Icon,
} from '@shopify/polaris';
import { useAuthFetch, useAuth } from '../AuthComponent';
import { downloadCsv, isoDaysAgo, todayIso, ReportFilters, ReportPicker, ReportColumnHelp } from '../Reporting';
import { ChevronRightIcon, ChevronDownIcon } from '@shopify/polaris-icons';

import { allRolesIn } from '../constants/roles';
const STATUS_LABEL = {
    open: 'Open', reopened: 'Reopened', received: 'Received', delivered: 'Delivered', exception: 'Exception',
    overdue: 'Overdue', partially_received: 'Partially received', out_for_delivery: 'Out for delivery',
    in_transit: 'In transit', shipped: 'Shipped', driving: 'Driving',
};
// Maps the API's `color` field to a Polaris Badge tone (green-outline gets the
// "partiallyComplete" progress ring instead of a distinct tone — Polaris has
// no outline variant).
const STATUS_BADGE = {
    green: { tone: 'success' },
    'green-outline': { tone: 'warning', progress: 'partiallyComplete' },   // Partially received: pastel orange (user, 2026-09-04)
    blue: { tone: 'info' },
    amber: { tone: 'attention' },
    red: { tone: 'critical' },
    grey: {},
};
const fmt = iso => iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }) : '';

// Date-only ISO (YYYY-MM-DD) → "Sep 8"; parsed by parts so it never shifts a day
// through the UTC midnight conversion `new Date('2026-09-08')` would do.
const fmtDay = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return '';
    return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
// Timestamp (with offset) → the Eastern calendar day. fmtDay above is for
// date-only strings; using it on a TIMESTAMPTZ drops the offset and can show
// the wrong day for an evening arrival (reference_date_only_tz_bug).
const fmtDayET = (iso) => {
    const d = new Date(iso || '');
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
};
// Row key: store-qualified when the backend supplies it (store=all merges
// every store, and box ids collide across the per-store tables).
const bk = (b) => b.key || String(b.id);
const upsTrackUrl = (num) => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(num)}&requester=ST/trackdetails`;

// Hand-carried boxes (2026-09-10): `service_code === 'HAND'`, no UPS tracking
// URL/eta ever. `_bulk_box_view`'s `service` field still resolves off the raw
// UPS service-code map (which has no 'HAND' entry), so the Service column
// renders off `service_code` directly rather than trusting `service`.
const isHandBox = (b) => b.service_code === 'HAND';
const serviceText = (b) => (isHandBox(b) ? 'Hand-carried' : (b.service || ''));
// "Driving" until the first order is scanned in at the destination store
// (ups_status_at gets stamped then, per the backend's arrival rule); after
// that, the arrival date.
const estDeliveryText = (b) => {
    if (!isHandBox(b)) {
        if (!b.tracking_number) return '';
        return b.ups_eta
            ? `${fmtDay(b.ups_eta)}${b.ups_status === 'delivered' ? ' (delivered)' : ''}`
            : (b.ups_status ? String(b.ups_status).replace(/_/g, ' ') : 'Awaiting first UPS scan');
    }
    return b.ups_status_at ? `Arrived ${fmtDayET(b.ups_status_at)} (first order scanned in)` : 'Driving';
};

const HEADINGS = [
    // Leading affordance so it reads as expandable (Eric, 2026-09-10). Not
    // sortable — SORT_KEYS is offset by one to match.
    { title: '', alignment: 'center' },
    // Created (Eric, 2026-10-20): when the box was first made — the useful date
    // on the Open / reopened table, where Ship date still reads "not shipped".
    { title: 'Created' },
    { title: 'Ship date' },
    { title: 'Tracking' },
    { title: 'Service' },
    { title: 'From' },
    { title: 'To store' },
    { title: 'Est. delivery' },
    { title: 'Orders', alignment: 'end' },
    { title: 'Received', alignment: 'end' },
    { title: 'Status' },
    { title: 'Shipped by' },
    { title: 'Days since shipped', alignment: 'end' },
];

// Sort keys per HEADINGS column (2026-09-09, Eric: sortable columns). Numbers sort
// numerically, dates/strings lexically; null/empty always sorts last.
const SORT_KEYS = [
    null,                                   // expand-arrow column, never sorts
    b => b.created_at || '',
    b => b.shipped_at || b.first_scanned_at || '',
    b => b.tracking_number || '',
    b => serviceText(b),
    b => b.from_location || b.from_location_id || '',
    b => b.to_location || b.to_location_id || '',
    b => b.ups_eta || '',
    b => Number(b.orders_count || 0),
    b => Number(b.received_count || 0),
    b => STATUS_LABEL[b.status] || b.status || '',
    b => b.shipped_by || '',
    b => (b.days_since_shipped == null ? null : Number(b.days_since_shipped)),
];
function sortBoxes(rows, col, dir) {
    const key = SORT_KEYS[col]; if (!key) return rows;
    const sign = dir === 'ascending' ? 1 : -1;
    return rows.slice().sort((a, b) => {
        const x = key(a), y = key(b);
        const xe = x === null || x === undefined || x === '', ye = y === null || y === undefined || y === '';
        if (xe && ye) return 0; if (xe) return 1; if (ye) return -1;
        if (typeof x === 'number' && typeof y === 'number') return sign * (x - y);
        return sign * String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
    });
}

// Hoisted to module scope (2026-09-04): defined inside the report's render it was a
// NEW component type on every render, so React unmounted and remounted the whole
// table on each selection — scroll jumped to the top and the checkbox states
// flickered. `ctx` carries the parent's state and callbacks.
function BoxTable({ rows, title, selectable = false, ctx, scrollBox = false, defaultSortCol = 2 }) {
    const { open, toggle, selectedBoxIds, allResourcesSelected, handleSelectionChange, shipping, markShipped, storeSlug, canShip, openRedirect, openDelete } = ctx;
    // Column sort — newest first on the caller's column (Ship date for the shipped
    // table, matching the server order; Created for the open / reopened table).
    const [sortCol, setSortCol] = useState(defaultSortCol);      // col 0 is the arrow, 1 Created, 2 Ship date
    const [sortDir, setSortDir] = useState('descending');
    const sortedRows = useMemo(() => sortBoxes(rows, sortCol, sortDir), [rows, sortCol, sortDir]);
    let rowIndex = 0;
    const selectionProps = selectable ? {
        selectable: true,
        selectedItemsCount: allResourcesSelected ? 'All' : selectedBoxIds.length,
        onSelectionChange: handleSelectionChange,
        promotedBulkActions: [{ content: shipping ? 'Marking…' : 'Mark as shipped', onAction: markShipped, disabled: shipping }],
    } : { selectable: false };
    return (
        <Card>
            <BlockStack gap="300">
                <Text variant="headingSm" as="h3">{title}</Text>
                {/* isolation: the IndexTable's sticky header / bulk-actions bar carry
                    Polaris z-indexes (100+) that would paint over the report's sticky
                    title + filter banners (z 18–21) when the page scrolls. A new
                    stacking context at z 0 keeps the whole table beneath them.
                    No overflow here: the IndexTable's own ScrollContainer scrolls
                    sideways, and an overflow-x:auto wrapper grew a nested vertical
                    scrollbar on classic-scrollbar Macs (2026-09-04). */}
                <div className={scrollBox ? 'bt-shipped' : undefined} style={{ position: 'relative', zIndex: 0, isolation: 'isolate' }}>
                    <IndexTable
                        resourceName={{ singular: 'box', plural: 'boxes' }}
                        itemCount={rows.length}
                        {...selectionProps}
                        headings={HEADINGS}
                        sortable={HEADINGS.map((_, i) => i !== 0)}
                        sortColumnIndex={sortCol}
                        sortDirection={sortDir}
                        onSort={(index, direction) => { setSortCol(index); setSortDir(direction); }}
                        emptyState={
                            <EmptySearchResult
                                title="No boxes"
                                description="Try widening the date range"
                                withIllustration
                            />
                        }
                    >
                        {sortedRows.flatMap(b => {
                            const state = open[bk(b)];
                            // 2026-09-08 (Eric): pastel red when UPS says the box was
                            // delivered but not every order in it has been scanned in
                            // at the store yet — those boxes need someone to go find them.
                            const deliveredNotReceived = b.ups_status === 'delivered'
                                && Number(b.received_count || 0) < Number(b.orders_count || 0);
                            const boxRow = (
                                <IndexTable.Row
                                    id={bk(b)}
                                    key={b.id}
                                    position={rowIndex++}
                                    tone={deliveredNotReceived ? 'critical' : undefined}
                                    selected={selectable ? selectedBoxIds.includes(bk(b)) : !!state}
                                    onClick={() => toggle(b)}
                                >
                                    <IndexTable.Cell>
                                        <span
                                            aria-hidden="true"
                                            style={{ display: 'inline-flex', alignItems: 'center',
                                                     color: 'var(--p-color-icon-secondary)' }}
                                        >
                                            <Icon source={state ? ChevronDownIcon : ChevronRightIcon} tone="subdued" />
                                        </span>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{fmt(b.created_at)}</IndexTable.Cell>
                                    <IndexTable.Cell>
                                        {fmt(b.shipped_at) || <Text tone="subdued">not shipped</Text>}
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        {isHandBox(b) ? (
                                            <Text as="span" fontWeight="medium">{b.tracking_number}</Text>
                                        ) : (
                                            <Link url={b.tracking_url} target="_blank" onClick={e => e.stopPropagation()}>
                                                <Text as="span" fontWeight="medium">{b.tracking_number}</Text>
                                            </Link>
                                        )}
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{serviceText(b)}</IndexTable.Cell>
                                    <IndexTable.Cell>{b.from_location || b.from_location_id}</IndexTable.Cell>
                                    <IndexTable.Cell>{b.to_location || b.to_location_id}</IndexTable.Cell>
                                    <IndexTable.Cell>
                                        {/* Until the UPS API is wired up (2026-09-05): a link to
                                            UPS's own tracking page, which shows the estimated
                                            delivery date. Falls back to the poller's status when
                                            that lands. A hand-carried box has no UPS tracking at
                                            all — plain "Driving" / arrival text, never a link. */}
                                        {isHandBox(b)
                                            ? estDeliveryText(b)
                                            : (b.tracking_number ? (
                                                <Link url={upsTrackUrl(b.tracking_number)} target="_blank" onClick={e => e.stopPropagation()}>
                                                    {estDeliveryText(b)}
                                                </Link>
                                            ) : '')}
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Text alignment="end" numeric>{b.orders_count}</Text>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Text alignment="end" numeric>{b.received_count} of {b.orders_count}</Text>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Badge {...(STATUS_BADGE[b.color] || {})}>{STATUS_LABEL[b.status] || b.status}</Badge>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{b.shipped_by || ''}</IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Text alignment="end" numeric>{b.days_since_shipped ?? ''}</Text>
                                    </IndexTable.Cell>
                                </IndexTable.Row>
                            );
                            if (!state) return [boxRow];

                            // Labelled subheader for the expanded order columns — without
                            // it, order rows sit directly under the box headings (Ship
                            // date / Tracking / Service / From / To store / Orders) and a
                            // customer name reads as a tracking number. The trailing 4
                            // cells (Orders/Received/Status/Shipped by/Days) stay empty so
                            // the 7 labels line up with the child rows' first 7 columns
                            // (all shifted right by the leading arrow column).
                            const subheaderRow = (
                                <IndexTable.Row id={`${b.id}-subheader`} key={`${b.id}-subheader`} position={rowIndex++} rowType="subheader" disabled>
                                    <IndexTable.Cell />
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Order</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Customer</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Pickup store</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Scanned into box</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Received at store</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Order status</Text></IndexTable.Cell>
                                    <IndexTable.Cell><Text tone="subdued" fontWeight="semibold">Status updated by</Text></IndexTable.Cell>
                                    <IndexTable.Cell>
                                        {canShip && !state.loading && !state.err && (
                                            <InlineStack gap="200" wrap={false}>
                                                <Button size="slim" onClick={() => openRedirect(b, state.orders)}>Change destination…</Button>
                                                <Button size="slim" tone="critical" variant="secondary"
                                                        onClick={() => openDelete(b, state.orders)}>Delete box…</Button>
                                            </InlineStack>
                                        )}
                                    </IndexTable.Cell>
                                </IndexTable.Row>
                            );

                            if (state.loading) {
                                return [boxRow, subheaderRow, (
                                    <IndexTable.Row id={`${b.id}-loading`} key={`${b.id}-loading`} position={rowIndex++} rowType="child" disabled>
                                        <IndexTable.Cell colSpan={12}><Spinner accessibilityLabel="Loading orders" size="small" /></IndexTable.Cell>
                                    </IndexTable.Row>
                                )];
                            }
                            if (state.err) {
                                return [boxRow, subheaderRow, (
                                    <IndexTable.Row id={`${b.id}-error`} key={`${b.id}-error`} position={rowIndex++} rowType="child" disabled>
                                        <IndexTable.Cell colSpan={12}><Text tone="critical">{state.err}</Text></IndexTable.Cell>
                                    </IndexTable.Row>
                                )];
                            }
                            const orderRows = state.orders.map(o => (
                                <IndexTable.Row id={`${b.id}-${o.order_id}`} key={o.order_id} position={rowIndex++} rowType="child" disabled>
                                    <IndexTable.Cell />
                                    <IndexTable.Cell>
                                        <Text as="span">
                                            <RouterLink to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>
                                                {o.order_name}
                                            </RouterLink>
                                        </Text>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{o.customer_name || ''}</IndexTable.Cell>
                                    <IndexTable.Cell>{o.pickup_store || ''}</IndexTable.Cell>
                                    <IndexTable.Cell>{fmt(o.scanned_at)}</IndexTable.Cell>
                                    <IndexTable.Cell>{fmt(o.received_at) || '—'}</IndexTable.Cell>
                                    <IndexTable.Cell>{o.order_status}</IndexTable.Cell>
                                    <IndexTable.Cell>{o.status_updated_by || ''}</IndexTable.Cell>
                                </IndexTable.Row>
                            ));
                            return [boxRow, subheaderRow, ...orderRows];
                        })}
                    </IndexTable>
                </div>
            </BlockStack>
        </Card>
    );
}


export default function BulkTransfersReport() {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    // Store managers see boxes heading to their stores; only shipping-side
    // roles can mark a box shipped (the API refuses it for store_manager too).
    // All-stores view is read-only: box ids are per store, and Mark as shipped /
    // Change destination address one store's tables.
    const { storeSlug } = useParams();
    const canShip = !allRolesIn(user, ['store_manager']) && storeSlug !== 'all';
    // ?box=<id> makes an expanded box linkable: the list request carries
    // box_id so the box is included even outside the date range, and it is
    // expanded once loaded. Toggling a box rewrites the param.
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedBoxId = searchParams.get('box') || '';
    const autoExpandedRef = useRef(null);
    // 2026-09-25 (Eric): a filtered view is linkable. Every filter reads from
    // the URL on load and writes back on change (replace, so the back button
    // is not spammed); ?box= is preserved. Dates are written only when they
    // differ from the default 30-day window, so a link without them always
    // means "the last 30 days as of opening" — what an emailed link needs.
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(30));
    const [dateTo, setDateTo] = useState(searchParams.get('to') || todayIso());
    const [fromLoc, setFromLoc] = useState(searchParams.get('from_loc') || '');
    const [toLoc, setToLoc] = useState(searchParams.get('to_loc') || '');
    const [status, setStatus] = useState(searchParams.get('status') || '');
    const [service, setService] = useState(searchParams.get('service') || '');   // 2026-09-25 (Eric): Service filter — UPS code or HAND
    // 2026-09-15 (Eric): boxes the store has fully received are hidden by
    // default; "Show Received" brings them back. Picking "Received" in the
    // Status filter shows them regardless.
    const [showReceived, setShowReceived] = useState(false);
    const [q, setQ] = useState(searchParams.get('q') || '');                    // raw input value (updates every keystroke)
    const [debouncedQ, setDebouncedQ] = useState('');   // drives the actual fetch, 300ms after typing stops
    const [boxes, setBoxes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState({});      // box id → {loading, orders, err}
    const [shipMsg, setShipMsg] = useState(null); // {tone, text} after "Mark as shipped"
    const [shipping, setShipping] = useState(false);
    // "Change destination" dialog: {box, orders, to} while open
    const [redirect, setRedirect] = useState(null);
    const [del, setDel] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [locations, setLocations] = useState([]);           // /api/locations rows, fetched once on mount
    const [baseline, setBaseline] = useState({ fromIds: [], toIds: [], names: {} }); // unfiltered from/to id sets (+ names) for the current date range

    // Debounce the search box (300ms), same pattern as PickingPortal.jsx.
    const debounceRef = useRef(null);
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(debounceRef.current);
    }, [q]);

    // /api/locations — once on mount. Gives {shopify_location_id, name, is_store, active}
    // for every location in this store, used to label the stable from/to selects below.
    useEffect(() => {
        let cancelled = false;
        fetchAuth('/api/locations').then(r => r.json()).then(rows => {
            if (!cancelled) setLocations(Array.isArray(rows) ? rows : []);
        }).catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAuth]);

    // Unfiltered baseline (date range only, no from/to/status/q) — the from/to
    // select options derive from this, not from the (possibly filtered-to-empty)
    // `boxes` list, so picking a status that yields zero rows doesn't blank the selects.
    useEffect(() => {
        let cancelled = false;
        const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        fetchAuth(`/api/reports/bulk-transfers/boxes?${p}`).then(r => r.json()).then(d => {
            if (cancelled) return;
            const rows = d.boxes || [];
            // Keep the names the API sends with every box. A store manager's
            // /api/locations only covers their own stores, so the Teams
            // fulfillment locations resolve to nothing and the from-filter
            // used to list a raw id like "71312965810" (Eric, 2026-09-10).
            const names = {};
            for (const b of rows) {
                if (b.from_location_id && b.from_location) names[String(b.from_location_id)] = b.from_location;
                if (b.to_location_id && b.to_location) names[String(b.to_location_id)] = b.to_location;
            }
            setBaseline({
                fromIds: [...new Set(rows.map(b => b.from_location_id))],
                toIds: [...new Set(rows.map(b => b.to_location_id))],
                names,
            });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [fetchAuth, dateFrom, dateTo]);

    const abortRef = useRef(null);
    const load = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            if (fromLoc) p.set('from_location_id', fromLoc);
            if (toLoc) p.set('to_location_id', toLoc);
            if (status) p.set('status', status);
            if (service) p.set('service', service);
            if (debouncedQ.trim()) p.set('q', debouncedQ.trim());
            if (linkedBoxId) p.set('box_id', linkedBoxId);
            const res = await fetchAuth(`/api/reports/bulk-transfers/boxes?${p}`, { signal: controller.signal });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const d = await res.json();
            if (abortRef.current !== controller) return; // superseded by a newer request
            setBoxes(d.boxes || []);
        } catch (e) {
            if (e.name === 'AbortError') return;
            if (abortRef.current === controller) setErr(e.message);
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, fromLoc, toLoc, status, service, debouncedQ, linkedBoxId]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const p = new URLSearchParams(searchParams);
        const set = (k, v, dflt = '') => { if (v && v !== dflt) p.set(k, v); else p.delete(k); };
        set('from', dateFrom, isoDaysAgo(30)); set('to', dateTo, todayIso());
        set('from_loc', fromLoc); set('to_loc', toLoc); set('status', status); set('service', service);
        set('q', q.trim());
        if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, fromLoc, toLoc, status, service, q]); // eslint-disable-line react-hooks/exhaustive-deps

    const setBoxParam = (id) => {
        const next = new URLSearchParams(searchParams);
        if (id) next.set('box', String(id)); else next.delete('box');
        setSearchParams(next, { replace: true });
    };
    const toggle = async (b) => {
        if (open[bk(b)]) { setOpen(o => { const c = { ...o }; delete c[bk(b)]; return c; }); if (String(b.id) === linkedBoxId) setBoxParam(null); return; }
        setBoxParam(b.id);
        setOpen(o => ({ ...o, [bk(b)]: { loading: true, orders: [] } }));
        try {
            const res = await fetchAuth(`/api/reports/bulk-transfers/boxes/${b.id}/orders${b.store_key ? `?store=${b.store_key}` : ''}`);
            const d = await res.json();
            setOpen(o => ({ ...o, [bk(b)]: { loading: false, orders: d.orders || [], err: res.ok ? null : `HTTP ${res.status}` } }));
        } catch (e) { setOpen(o => ({ ...o, [bk(b)]: { loading: false, orders: [], err: e.message } })); }
    };

    // Deep link: expand the linked box once it is in the list (once per id).
    useEffect(() => {
        if (!linkedBoxId || autoExpandedRef.current === linkedBoxId) return;
        const b = boxes.find(x => String(x.id) === linkedBoxId);
        if (!b) return;
        autoExpandedRef.current = linkedBoxId;
        if (!open[bk(b)]) toggle(b);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boxes, linkedBoxId]);

    const locById = useMemo(() => new Map(locations.map(l => [String(l.shopify_location_id), l])), [locations]);
    const storeOptions = useMemo(() => locations
        .filter(l => l.is_store && l.active !== false && l.shopify_location_id && String(l.shopify_location_id) !== '0')
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(l => ({ label: l.name, value: String(l.shopify_location_id) })), [locations]);
    // Delete a box created by mistake. Destructive, so the modal makes the
    // operator type DELETE before the button turns on (Eric, 2026-09-10).
    const openDelete = (b, orders) => setDel({ box: b, orders: orders || [], word: '' });
    const submitDelete = async () => {
        if (!del || deleting || del.word.trim().toUpperCase() !== 'DELETE') return;
        setDeleting(true);
        try {
            const res = await fetchAuth(`/api/reports/bulk-transfers/boxes/${del.box.id}/delete${del.box.store_key ? `?store=${del.box.store_key}` : ''}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirm: del.word.trim() }),
            });
            const d = await res.json();
            if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${res.status}`);
            const n = d.detached_orders || 0;
            setShipMsg({ tone: 'success',
                text: `Box ${del.box.tracking_number} deleted` + (n ? `; ${n} order${n === 1 ? '' : 's'} released from it.` : '.') });
            setDel(null);
            clearSelection();
            load();
        } catch (e) {
            setShipMsg({ tone: 'critical', text: `Could not delete the box: ${e.message}` });
        } finally { setDeleting(false); }
    };

    const openRedirect = (b, orders) => {
        // Preselect the store the box's orders belong to, when they agree.
        const names = [...new Set((orders || []).map(o => o.pickup_store).filter(Boolean))];
        const match = names.length === 1 ? locations.find(l => l.name === names[0]) : null;
        setRedirect({ box: b, orders: orders || [], to: match ? String(match.shopify_location_id) : String(b.to_location_id) });
    };
    const submitRedirect = async () => {
        if (!redirect || redirecting) return;
        setRedirecting(true);
        try {
            const res = await fetchAuth(`/api/reports/bulk-transfers/boxes/${redirect.box.id}/destination${redirect.box.store_key ? `?store=${redirect.box.store_key}` : ''}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ to_location_id: redirect.to }),
            });
            const d = await res.json();
            if (res.status === 409 && d.detail?.error === 'orders_other_store') {
                setShipMsg({ tone: 'critical', text: `Not changed: ${d.detail.orders.join(', ')} ${d.detail.orders.length === 1 ? 'does' : 'do'} not belong to ${d.detail.store}.` });
            } else if (!res.ok) {
                throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${res.status}`);
            } else if (d.changed) {
                setShipMsg({ tone: 'success', text: `${redirect.box.tracking_number} now goes to ${d.to_store} (was ${d.from_store}); ${d.orders} order${d.orders === 1 ? '' : 's'} updated.` });
            } else {
                setShipMsg({ tone: 'info', text: `${redirect.box.tracking_number} already goes to that store.` });
            }
            setRedirect(null);
            setOpen({});
            load();
        } catch (e) {
            setShipMsg({ tone: 'critical', text: `Could not change destination: ${e.message}` });
        } finally { setRedirecting(false); }
    };
    // Locations list first (it has the canonical name), then the name the box
    // rows carried, then the bare id as a last resort.
    const nameFor = (id) => locById.get(String(id))?.name || baseline.names?.[String(id)] || id;

    // from-options: active locations that appear as a `from` in the unfiltered
    // (date-range-only) baseline. Keeps a currently-selected value listed even
    // if it drops out of the baseline (e.g. a stale URL param), so it stays
    // clearable rather than silently vanishing.
    const fromOptions = useMemo(() => {
        const ids = baseline.fromIds.filter(id => locById.get(String(id))?.active !== false);
        const opts = ids.map(id => [id, nameFor(id)]);
        if (fromLoc && !ids.includes(fromLoc)) opts.push([fromLoc, nameFor(fromLoc)]);
        opts.sort((a, b) => String(a[1]).localeCompare(String(b[1])));
        return opts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseline.fromIds, baseline.names, locById, fromLoc]);

    const toOptions = useMemo(() => {
        const opts = baseline.toIds.map(id => [id, nameFor(id)]);
        if (toLoc && !baseline.toIds.includes(toLoc)) opts.push([toLoc, nameFor(toLoc)]);
        return opts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseline.toIds, baseline.names, locById, toLoc]);

    const receivedHidden = !showReceived && status !== 'received';
    const receivedCount = boxes.filter(b => b.status === 'received').length;
    const visibleBoxes = receivedHidden ? boxes.filter(b => b.status !== 'received') : boxes;
    const shipped = visibleBoxes.filter(b => b.box_state !== 'open' && b.box_state !== 'reopened');
    const notShipped = visibleBoxes.filter(b => b.box_state === 'open' || b.box_state === 'reopened');

    // Selection on the open / reopened table only (the shipped table stays
    // read-only). Polaris keeps the selected ids; "Mark as shipped" posts them
    // to the report endpoint, which applies the same rule as the portal's
    // Ship box button (skips empty and already-shipped boxes) and stamps the
    // signed-in username as "shipped by".
    // The rows are keyed `<store>:<id>` (bk), so the selection state must be
    // registered under the SAME key. It used to register bare `b.id`, so nothing
    // Polaris handed back ever matched: no checkbox ever looked selected and
    // "Mark as shipped" always posted an empty list (Eric, 2026-09-10).
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState(notShipped.map(b => ({ id: bk(b) })));
    // Only box rows count as "selected" (expanded order rows are disabled, but
    // keep the count honest regardless of what Polaris hands back).
    const selectedBoxKeys = selectedResources.filter(k => notShipped.some(b => bk(b) === String(k)));
    const selectedBoxIds = selectedBoxKeys;
    const markShipped = async () => {
        // Only real box rows: an expanded box adds child/subheader rows whose ids
        // are "<box>-<order>" / "<box>-subheader"; those must never reach the API
        // (they produced HTTP 422, 2026-09-04). The table keys rows `<store>:<id>`,
        // so map back to the numeric id the endpoint expects.
        const ids = selectedBoxKeys
            .map(k => notShipped.find(b => bk(b) === k))
            .filter(Boolean)
            .map(b => String(b.id));
        if (!ids.length || shipping) return;
        setShipping(true); setShipMsg(null);
        try {
            const res = await fetchAuth('/api/reports/bulk-transfers/boxes/ship', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ box_ids: ids }),
            });
            const d = await res.json();
            if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${res.status}`);
            const n = (d.shipped || []).length;
            const skipped = d.skipped || [];
            const reasons = skipped.map(s => `${notShipped.find(b => String(b.id) === String(s.id))?.tracking_number || s.id} (${s.reason === 'empty' ? 'no orders' : 'already shipped'})`);
            setShipMsg({
                tone: n ? (skipped.length ? 'warning' : 'success') : 'warning',
                text: `${n} box${n === 1 ? '' : 'es'} marked as shipped` + (reasons.length ? `. Skipped: ${reasons.join(', ')}` : '.'),
            });
            clearSelection();
            load();
        } catch (e) {
            setShipMsg({ tone: 'critical', text: `Could not mark as shipped: ${e.message}` });
        } finally { setShipping(false); }
    };

    const exportCsv = () => {
        const rows = [['Created', 'Ship date', 'Tracking', 'Service', 'From', 'To store', 'Orders', 'Received', 'Status', 'Shipped by', 'Days since shipped', 'UPS status', 'Est. delivery']];
        // Est. delivery: hand-carried rows get the "Driving" / "Arrived <M/D>…"
        // text (they have no UPS eta at all); UPS rows keep the raw eta date
        // they always exported, unchanged.
        for (const b of shipped) rows.push([fmt(b.created_at), fmt(b.shipped_at), b.tracking_number, serviceText(b), b.from_location || '', b.to_location || '',
            b.orders_count, b.received_count, STATUS_LABEL[b.status] || b.status, b.shipped_by || '', b.days_since_shipped ?? '', b.ups_status || '',
            isHandBox(b) ? estDeliveryText(b) : (b.ups_eta || '')]);
        downloadCsv(`bulk_transfers_${dateFrom}_to_${dateTo}.csv`, rows);
    };

    const statusOptions = [{ label: 'All statuses', value: '' },
        ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ label, value }))];
    // Service filter (Eric, 2026-09-25): "driving" boxes are service_code HAND;
    // the rest are UPS service codes (bulk_shipping.UPS_SERVICE_NAMES).
    const serviceOptions = [
        { label: 'All services', value: '' },
        { label: 'Driving (hand-carried)', value: 'HAND' },
        { label: 'UPS Ground', value: '03' },
        { label: 'UPS 3 Day Select', value: '12' },
        { label: 'UPS 2nd Day Air', value: '02' },
        { label: 'UPS 2nd Day Air A.M.', value: '59' },
        { label: 'UPS Next Day Air', value: '01' },
        { label: 'UPS Next Day Air Saver', value: '13' },
        { label: 'UPS Next Day Air Early', value: '14' },
    ];
    // Alphabetical (Eric, 2026-09-17) — the lists used to come out in box order.
    const _byName = (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' });
    const fromSelectOptions = [{ label: 'All from-locations', value: '' },
        ...fromOptions.map(([id, n]) => ({ label: n || id, value: id })).sort(_byName)];
    const toSelectOptions = [{ label: 'All stores', value: '' },
        ...toOptions.map(([id, n]) => ({ label: n || id, value: id })).sort(_byName)];


    // 2026-09-25 (Eric): everything above the Shipped boxes table stays put.
    // Polaris pins IndexTable headings only against the table's OWN scroll
    // box, never the page — so the shipped table scrolls inside its card and
    // the page itself stops needing to. The box's height is measured, not
    // guessed: viewport bottom minus where the table starts, re-measured
    // whenever the region above changes (open-box count, wrapping filters).
    const reportRef = useRef(null);
    useLayoutEffect(() => {
        const root = reportRef.current;
        if (!root) return undefined;
        const measure = () => {
            const box = root.querySelector('.bt-shipped .Polaris-IndexTable-ScrollContainer');
            const scroller = root.closest('.page-content');
            if (!box || !scroller) return;
            const top = box.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
            let max = Math.max(240, scroller.clientHeight - top - 24);
            box.style.setProperty('--bt-shipped-max', `${Math.round(max)}px`);
            // Second pass: whatever the page can still scroll by (its own
            // bottom padding, the card's edge — measured 29px) comes off the
            // table, so a wheel over the header has nowhere to move it.
            // Only once: any slack left after this is not the table's (on a
            // short window it is the report list in the sidebar), and chasing
            // it would only shrink the table.
            const slack = scroller.scrollHeight - scroller.clientHeight;
            if (slack > 0 && max - slack >= 240) {
                max -= slack;
                box.style.setProperty('--bt-shipped-max', `${Math.round(max)}px`);
            }
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(root);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [shipped.length, notShipped.length, loading]);

    const tableCtx = { open, toggle, selectedBoxIds, allResourcesSelected, handleSelectionChange, shipping, markShipped, storeSlug, canShip, openRedirect, openDelete };
    return (
        <BlockStack gap="400">
          <div className="bt-report" ref={reportRef}>
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Bulk Transfers" storageKey="bulk_transfers_about_open" linkLabel="About this report">
                        <Text as="p">{canShip ? 'Bulk boxes shipped from the Teams fulfillment locations to the stores.' : 'Bulk boxes on their way to your store.'} Click a box to see its orders; click an order to open it.</Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={[]}
                        locationFilter={[]}
                        setLocationFilter={() => {}}
                        onExport={exportCsv}
                        exportDisabled={loading || shipped.length === 0}
                        exportLabel="Export CSV"
                    />
                    <InlineStack gap="300" wrap>
                        <ReportPicker label="From location" options={fromSelectOptions} value={fromLoc} onChange={setFromLoc} searchable />
                        <ReportPicker label="To store" options={toSelectOptions} value={toLoc} onChange={setToLoc} searchable />
                        <ReportPicker label="Status" options={statusOptions} value={status} onChange={setStatus} />
                        <ReportPicker label="Service" options={serviceOptions} value={service} onChange={setService} />
                        <TextField
                            label="Search boxes" labelHidden
                            type="search"
                            placeholder="Tracking or order #"
                            value={q}
                            onChange={setQ}
                            autoComplete="off"
                            clearButton
                            onClearButtonClick={() => setQ('')}
                        />
                        <Button
                            pressed={showReceived}
                            disabled={status === 'received'}
                            onClick={() => setShowReceived(v => !v)}
                            title={showReceived ? 'Hide boxes the store has fully received' : 'Show boxes the store has fully received'}
                        >
                            {showReceived ? 'Hide Received' : `Show Received${receivedCount ? ` (${receivedCount})` : ''}`}
                        </Button>
                    </InlineStack>
                    {err && <Text tone="critical">{err}</Text>}
                </BlockStack>
            </Card>
            {/* The tables live OUTSIDE the first card on purpose: the reporting
                CSS pins `.reporting-content > .Polaris-BlockStack > .Polaris-ShadowBevel:first-child`
                as the sticky filter card (z 19). Anything inside it paints over
                the sticky page-title banner while scrolling. */}
            {/* Open / reopened boxes first (2026-09-05): that is the table with
                work to do (select → Mark as shipped); shipped history below. */}
            {notShipped.length > 0 && <BoxTable rows={notShipped} title={`Open / reopened boxes (${notShipped.length})`} selectable={canShip} ctx={tableCtx} defaultSortCol={1} />}
            {shipMsg && <Banner tone={shipMsg.tone} onDismiss={() => setShipMsg(null)}>{shipMsg.text}</Banner>}
            <BoxTable rows={shipped} title={`Shipped boxes (${shipped.length})`} ctx={tableCtx} scrollBox />
            {del && (
                <Modal
                    open
                    onClose={() => setDel(null)}
                    title={`Delete box ${del.box.tracking_number}?`}
                    primaryAction={{ content: 'Delete this box', destructive: true, onAction: submitDelete,
                                     loading: deleting,
                                     disabled: del.word.trim().toUpperCase() !== 'DELETE' }}
                    secondaryActions={[{ content: 'Cancel', onAction: () => setDel(null) }]}
                >
                    <Modal.Section>
                        <BlockStack gap="300">
                            <Text>
                                This removes the box from {del.box.from_location || del.box.from_location_id}
                                {' '}to <Text as="span" fontWeight="semibold">{del.box.to_location || del.box.to_location_id}</Text>.
                                {' '}It cannot be undone.
                            </Text>
                            {del.orders.length > 0 ? (
                                <Banner tone="warning">
                                    <BlockStack gap="100">
                                        <Text as="p" fontWeight="semibold">
                                            {del.orders.length} order{del.orders.length === 1 ? '' : 's'} will be released from this box.
                                        </Text>
                                        <Text as="p">
                                            {del.orders.slice(0, 8).map(o => o.order_name).filter(Boolean).join(', ')}
                                            {del.orders.length > 8 ? `, and ${del.orders.length - 8} more` : ''}
                                        </Text>
                                        <Text as="p" tone="subdued">
                                            They stay transferred to the store — only the box tracking comes off the order, its metafield and its tag.
                                        </Text>
                                    </BlockStack>
                                </Banner>
                            ) : (
                                <Text tone="subdued">The box is empty, so no orders are affected.</Text>
                            )}
                            <TextField
                                label="Type DELETE to confirm"
                                value={del.word}
                                onChange={v => setDel(d => ({ ...d, word: v }))}
                                autoComplete="off"
                                placeholder="DELETE"
                            />
                        </BlockStack>
                    </Modal.Section>
                </Modal>
            )}
            {redirect && (
                <Modal
                    open
                    onClose={() => setRedirect(null)}
                    title={`Change destination for ${redirect.box.tracking_number}`}
                    primaryAction={{ content: 'Change destination', onAction: submitRedirect, loading: redirecting,
                                     disabled: !redirect.to || String(redirect.to) === String(redirect.box.to_location_id) }}
                    secondaryActions={[{ content: 'Cancel', onAction: () => setRedirect(null) }]}
                >
                    <Modal.Section>
                        <BlockStack gap="300">
                            <Text>
                                Currently going to <Text as="span" fontWeight="semibold">{redirect.box.to_location || redirect.box.to_location_id}</Text>.
                                {' '}{redirect.orders.length} order{redirect.orders.length === 1 ? '' : 's'} in the box
                                {redirect.orders.length ? ` for ${[...new Set(redirect.orders.map(o => o.pickup_store).filter(Boolean))].join(', ') || 'unknown stores'}` : ''}.
                            </Text>
                            <Select label="New destination store" options={storeOptions} value={redirect.to}
                                    onChange={v => setRedirect(r => ({ ...r, to: v }))} />
                            <Text tone="subdued">The box and every order in it are updated, and each order's timeline gets a "Bulk Box Destination Changed" entry. A store the orders do not belong to is refused.</Text>
                        </BlockStack>
                    </Modal.Section>
                </Modal>
            )}
          </div>
        </BlockStack>
    );
}
